import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressButton from '@/components/common/progress-button';
import TabList from '@/components/common/tablist';
import SortContactDropdown from '@/components/contacts/contact-list/sort-contact-dropdown';
import type { Props as AppHeaderProps } from '@/components/layouts/page-layout/app-header';
import PageLayout, { type PageLayoutProps } from '@/components/layouts/page-layout/page-layout';
import ForwardSessionList from '@/components/sessions/session-list/forward-session-list';
import useForwardList, { type ForwardListItem, type ForwardListType } from '@/hooks/contacts/use-forward-list';
import type { ForwardTarget } from '@/hooks/forward/use-batch-forward';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import { useConfigurationStore } from '@/stores/configuration';
import { addTranslateToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';
import SearchInput from '../common/search-input';
import SessionCarousel from '../conversations/session-carousel';

type Props = {
  onForward: (targets: ForwardTarget[], clearSelection: () => void) => void;
  isLoading?: boolean;
} & Pick<PageLayoutProps, 'title' | 'className'> &
  Pick<AppHeaderProps, 'navigateBack'>;

export default function ForwardForm({ onForward, title, className, isLoading, navigateBack }: Props) {
  const { t } = useTranslation();
  const { isDesktop } = useUserPlatform();
  const [selectedSessions, setSelectedSessions] = useState<Map<string, ForwardListItem>>(new Map());
  const { options, searchTerm, listType, setSearchTerm, setListType, sortBy, setSortBy } = useForwardList();

  const selectedSessionCount = useMemo(() => {
    let count = 0;
    for (const s of selectedSessions.values()) {
      count += s.type === 'tag' ? s.userIds.length : 1;
    }
    return count;
  }, [selectedSessions]);

  const handleChangeSelections = (sessions: ForwardListItem[], selected: boolean) => {
    const maxForwardCount = useConfigurationStore.getState().envConfig.config?.maxForwardSessionCount ?? 100;

    setSelectedSessions((prev) => {
      const map = new Map(prev);
      let newSessionCount = selectedSessionCount;

      for (const s of sessions) {
        const additionalSessionCount = s.type === 'tag' ? s.userIds.length : 1;

        if (selected) {
          if (newSessionCount + additionalSessionCount > maxForwardCount) {
            addTranslateToast({
              type: 'warning',
              message: `errors.conversation.length|max=${maxForwardCount}`,
            });
            break;
          }
          map.set(s.key, s);
          newSessionCount += additionalSessionCount;
        } else {
          map.delete(s.key);
          newSessionCount -= additionalSessionCount;
        }
      }
      return map;
    });
  };

  const handleClearSelections = () => {
    setSelectedSessions(new Map());
  };

  const handleSend = () => onForward(Array.from(selectedSessions.values()), handleClearSelections);

  useAdjustLayout({ inputTargetSelector: '#search-input' });

  return (
    <PageLayout
      className={cn(
        'w-full group-[.modal-layout]:h-75vh group-[.modal-layout]:max-h-[800px] mode-embedded:pb-safe-offset-[0rem]',
        className,
      )}
      title={title}
      headerConfig={{
        navigateBack,
        className: 'desktop:px-4',
        actions: (
          <ProgressButton
            as='button'
            isLoading={isLoading}
            disabled={selectedSessions.size === 0}
            onClick={handleSend}
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            label='conversation.forward.send'
          />
        ),
      }}>
      {!!selectedSessions.size && (
        <div className='flex flex-col w-full items-stretch gap-2'>
          <h5 className='mx-5 text-body2 desktop:mx-4'>{`${t('upload.selected')} (${selectedSessionCount})`}</h5>
          <SessionCarousel
            sessions={Array.from(selectedSessions.values())}
            className='mx-5 desktop:mx-4'
            onRemove={(key) => handleChangeSelections([key], false)}
          />
        </div>
      )}

      <SearchInput
        keyword={searchTerm}
        setKeyword={setSearchTerm}
        clearKeyword={searchTerm ? () => setSearchTerm('') : undefined}
        className='m-0 mx-5'
        icon
      />
      <TabList
        defaultActiveTab={listType}
        tabs={(isDesktop ? ['recent', 'chats', 'groups'] : ['recent', 'chats', 'groups', 'tags']).map((item) => ({
          key: item,
          label: `conversation.forward.${item}`,
        }))}
        onClickTab={(key) => setListType(key as ForwardListType)}
      />

      <div className='flex flex-1 flex-col gap-2 desktop:overflow-y-auto'>
        <ForwardSessionList
          key={listType}
          sessions={options}
          selectedSessions={selectedSessions}
          onSelectSessions={handleChangeSelections}>
          {listType === 'chats' && (
            <SortContactDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          )}
        </ForwardSessionList>
      </div>
    </PageLayout>
  );
}
